import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { SupplierName } from "@/components/SupplierName";
import { FileSpreadsheet, FileDown, Eye, Download, Trash2, Pencil } from "lucide-react";
import PayProofDialog from "@/components/PayProofDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DescCell = ({ text, i }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="block max-w-[150px] truncate text-left hover:text-blue-600 transition-colors" data-testid={`ledger-desc-${i}`}>{text}</button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-80 rounded-2xl p-4" data-testid={`ledger-desc-full-${i}`}>
      <div className="text-xs font-semibold text-slate-800 mb-2">Keterangan lengkap</div>
      <div className="max-h-64 overflow-y-auto text-xs leading-relaxed text-slate-600 whitespace-normal break-words">{text}</div>
    </PopoverContent>
  </Popover>
);
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TYPES = [["all", "Semua Transaksi"], ["pay", "Pembayaran saja"], ["nota", "Nota saja"], ["retur", "Potongan Retur"]];

const fetchProof = async (id) => {
  const r = await api.get(`/proofs/${id}`, { responseType: "blob" });
  const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `bukti-${id}`;
  return { url: URL.createObjectURL(r.data), name };
};

const ProofButtons = ({ id, i }) => {
  const run = async (dl) => {
    try {
      const { url, name } = await fetchProof(id);
      if (dl) { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Gagal membuka bukti transfer"); }
  };
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => run(false)} data-testid={`ledger-proof-view-${i}`}><Eye className="h-3.5 w-3.5 mr-1" />Lihat</Button>
      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => run(true)} data-testid={`ledger-proof-download-${i}`}><Download className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

export default function Ledger() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sup, setSup] = useState("all");
  const [type, setType] = useState("pay");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  useEffect(() => { api.get(`/ledger${sup !== "all" ? `?supplier_id=${sup}` : ""}`).then((r) => setRows(r.data)); }, [sup]);
  const { user } = useAuth();
  const canEdit = user?.role !== "staff";
  const [editDlg, setEditDlg] = useState(null);
  const reload = () => api.get(`/ledger${sup !== "all" ? `?supplier_id=${sup}` : ""}`).then((r) => setRows(r.data));
  const unpay = async (r) => {
    if (!window.confirm(`Hapus pencatatan ${r.type} ${r.ref} sebesar ${formatRupiah(r.credit)}?\nJadwal kembali ke Pembayaran Terjadwal dan bisa dicatat ulang (dengan bukti transfer).`)) return;
    try {
      await api.post(`/schedules/${r.sid}/unpay`, { tranche_id: r.tid || null });
      toast.success("Pencatatan dihapus. Catat ulang di Pembayaran Terjadwal.");
      const res = await api.get(`/ledger${sup !== "all" ? `?supplier_id=${sup}` : ""}`); setRows(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const shown = rows.filter((r) => (type === "all" || (type === "pay" && r.type.startsWith("Pembayaran")) || (type === "nota" && r.type === "Nota") || (type === "retur" && r.type === "Potongan Retur"))
    && (!from || r.date >= from) && (!to || r.date <= to));
  const totPaid = shown.filter((r) => r.type.startsWith("Pembayaran")).reduce((a, r) => a + r.credit, 0);
  const head = ["Tanggal", "Jenis", "Ref", "Supplier", "Keterangan", "Debit (Nota)", "Kredit (Bayar/Retur)", "Saldo", "Dicatat oleh"];
  const body = shown.map((r) => [formatDate(r.date), r.type, r.ref, r.supplier, r.desc, r.debit || "", r.credit || "", r.balance, r.by]);
  const fname = `buku-besar-${new Date().toISOString().slice(0, 10)}`;

  const toExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([head, ...body, [], ["", "", "", "", "Total dibayar", "", totPaid]]);
    ws["!cols"] = [12, 24, 14, 22, 40, 16, 18, 16, 18].map((w) => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Buku Besar");
    XLSX.writeFile(wb, `${fname}.xlsx`);
  };
  const toPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(13); doc.text("Buku Besar Pembayaran - CV. Meranggi Jaya", 14, 14);
    doc.setFontSize(9); doc.text(`Periode: ${from ? formatDate(from) : "awal"} - ${to ? formatDate(to) : "sekarang"} · Total dibayar: ${formatRupiah(totPaid)}`, 14, 20);
    autoTable(doc, { startY: 25, head: [head], styles: { fontSize: 7.5 }, headStyles: { fillColor: [37, 99, 235] },
      body: shown.map((r) => [formatDate(r.date), r.type, r.ref, r.supplier, r.desc, r.debit ? formatRupiah(r.debit) : "", r.credit ? formatRupiah(r.credit) : "", formatRupiah(r.balance), r.by]) });
    doc.save(`${fname}.pdf`);
  };

  return (
    <PageShell title="Buku Besar">
      <PayProofDialog target={editDlg} onClose={() => setEditDlg(null)} onDone={() => { setEditDlg(null); reload(); }} />
      <div className="flex flex-wrap items-center gap-2">
        <Select value={sup} onValueChange={setSup}>
          <SelectTrigger className="w-48" data-testid="ledger-supplier"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Supplier</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-44" data-testid="ledger-type"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="w-44"><DatePicker value={from} onChange={setFrom} placeholder="Dari tanggal" testid="ledger-from" /></div>
        <div className="w-44"><DatePicker value={to} onChange={setTo} placeholder="Sampai tanggal" testid="ledger-to" /></div>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={toExcel} data-testid="ledger-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" onClick={toPdf} data-testid="ledger-export-pdf"><FileDown className="h-4 w-4 mr-1.5" />PDF</Button>
        </div>
      </div>
      <div className="total-box rounded-2xl px-5 py-4 w-fit" data-testid="ledger-total-paid"><div className="text-xs text-slate-400">Total dibayar (sesuai filter)</div><div className="font-bold">{formatRupiah(totPaid)}</div></div>
      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Ref</TableHead><TableHead>Supplier</TableHead><TableHead>Keterangan</TableHead>
            <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead>Dicatat oleh</TableHead><TableHead>Bukti</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((r, i) => (
              <TableRow key={i} data-testid={`ledger-row-${i}`}>
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.type === "Nota" ? "bg-zinc-100 text-zinc-700" : r.type === "Potongan Retur" ? "bg-purple-50 text-purple-700" : "bg-emerald-50 text-emerald-700"}`}>{r.type}</span></TableCell>
                <TableCell>{r.ref}</TableCell>
                <TableCell><SupplierName name={r.supplier} /></TableCell>
                <TableCell><DescCell text={r.desc} i={i} /></TableCell>
                <TableCell className="text-right">{r.debit ? formatRupiah(r.debit) : "-"}</TableCell>
                <TableCell className="text-right text-emerald-700">{r.credit ? formatRupiah(r.credit) : "-"}</TableCell>
                <TableCell className="text-right font-semibold">{formatRupiah(r.balance)}</TableCell>
                <TableCell className="text-slate-500">{r.by}</TableCell>
                <TableCell>{r.proof_id ? <ProofButtons id={r.proof_id} i={i} /> : <span className="text-slate-300">-</span>}</TableCell>
                <TableCell>{r.sid && canEdit && <div className="flex gap-1"><Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditDlg({ s: { id: r.sid }, t: r.tid ? { id: r.tid } : null, edit: r })} data-testid={`ledger-edit-${i}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button><Button size="sm" variant="outline" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => unpay(r)} title="Hapus pencatatan pembayaran" data-testid={`ledger-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>}</TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Belum ada transaksi.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </PageShell>
  );
}
